import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import nanoid from 'nanoid';
import { Checkbox } from '@mbkit/checkbox';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Select_select_djFYxd5YXe{box-sizing:border-box;-webkit-appearance:none;-moz-appearance:none;appearance:none;display:block;width:100%;font-family:var(--font-family,\"Averta\",\"Helvetica Neue\",\"Helvetica\",\"Arial\",\"sans-serif\");background:var(--background,#fff);color:var(--foreground,#2d2d2d);border:1px solid var(--neutral-light,#d5d9de);border-radius:var(--radius,3px);padding:8px;font-size:var(--font-spec-5-size,1rem);outline:none;cursor:pointer;transition:box-shadow .1s ease-out,border .2s ease-out;background-repeat:no-repeat;background-position:calc(100% - 12px) 50%;background-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Cpath fill='encodeHexColor(%232D2D2D)' d='M12.813 5.485l.624.78-5.312 4.25-5.312-4.25.624-.78 4.688 3.75z' fill-rule='evenodd'/%3E%3C/svg%3E\")}.Select_select_djFYxd5YXe:not(:disabled):focus{border-color:var(--secondary-base,#0a7c8e);box-shadow:0 0 0 4px var(--focus,rgba(96,150,230,.75))}.Select_select_djFYxd5YXe:not(:disabled).Select_invalid_2wK3GWfyL8{border-color:var(--error-base,#d10f0f)}.Select_select_djFYxd5YXe:disabled{background-color:var(--neutral-light,#d5d9de);border-color:var(--neutral-light,#d5d9de);color:var(--neutral-darker,#696c74);cursor:not-allowed;background-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Cpath fill='encodeHexColor(%23696C74)' d='M12.813 5.485l.624.78-5.312 4.25-5.312-4.25.624-.78 4.688 3.75z' fill-rule='evenodd'/%3E%3C/svg%3E\")}.Select_multiSelectContainer_2WYJMKoUlu{position:relative}.Select_multiSelectContainer_2WYJMKoUlu .Select_select_djFYxd5YXe.Select_isOpen_38mZCpajH3{border-bottom-color:var(--secondary-base,#0a7c8e);border-color:var(--secondary-base,#0a7c8e) var(--secondary-base,#0a7c8e) var(--neutral-lighter,#eff1f3);border-radius:var(--radius,3px) var(--radius,3px) 0 0}.Select_multiSelectMenu_1hSkCqbA6y{box-sizing:border-box;position:absolute;top:100%;left:0;width:100%;opacity:0;pointer-events:none;background:var(--background,#fff);visibility:hidden;border:1px solid var(--secondary-base,#0a7c8e);border-top:none;border-radius:0 0 var(--radius,3px) var(--radius,3px);z-index:1;max-height:200px;overflow:scroll;display:none}.Select_multiSelectMenu_1hSkCqbA6y.Select_isOpen_38mZCpajH3{opacity:1;pointer-events:auto;visibility:visible;display:block}.Select_multiSelectItem_2MEsvP4DZ1{display:flex;align-items:center;padding:4px 8px}.Select_multiSelectItem_2MEsvP4DZ1:first-child{margin-top:4px}.Select_multiSelectItem_2MEsvP4DZ1:last-child{margin-bottom:4px}.Select_multiSelectItem_2MEsvP4DZ1 .Select_checkbox_2wyr9ml_ld{margin-right:8px;flex:0 0 32px;height:32px}.Select_placeholder_1yAtLyqzYV{color:var(--neutral-darker,#696c74)}.Select_selectPlaceholderLabel_34lhro1TtJ{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;padding-right:24px}.Select_label_1bj-f6GMON{color:var(--neutral-darker,#696c74);font-size:var(--font-spec-6-size,.75rem);line-height:1.5;text-indent:8px}";
var styles = {"select":"Select_select_djFYxd5YXe","invalid":"Select_invalid_2wK3GWfyL8","multiSelectContainer":"Select_multiSelectContainer_2WYJMKoUlu","isOpen":"Select_isOpen_38mZCpajH3","multiSelectMenu":"Select_multiSelectMenu_1hSkCqbA6y","multiSelectItem":"Select_multiSelectItem_2MEsvP4DZ1","checkbox":"Select_checkbox_2wyr9ml_ld","placeholder":"Select_placeholder_1yAtLyqzYV","selectPlaceholderLabel":"Select_selectPlaceholderLabel_34lhro1TtJ","label":"Select_label_1bj-f6GMON"};
styleInject(css_248z);

const Select = React.forwardRef((props, ref) => {
    const { className = '', invalid } = props, rest = __rest(props, ["className", "invalid"]);
    const classNames = classnames({
        [styles.select]: true,
        [styles.invalid]: invalid,
        [className]: className,
    });
    return React.createElement("select", Object.assign({}, rest, { className: classNames, ref: ref }));
});
Select.displayName = 'Select';

const MultiSelectItem = (props) => {
    const { label } = props, rest = __rest(props, ["label"]);
    return (React.createElement("label", { className: styles.multiSelectItem },
        React.createElement(Checkbox, Object.assign({ "data-multiselect-item": true, type: "checkbox", role: "option", "aria-selected": props.checked ? true : false, "aria-checked": props.checked, "aria-label": label, tabIndex: -1, className: styles.checkbox }, rest)),
        React.createElement("span", null, label)));
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const noop = (args) => { };
const MultiSelect = (props) => {
    const { options, label, onChange, className = '', placeholder = 'Select option(s)', onClick = noop, selectedOptionsLabel } = props, rest = __rest(props, ["options", "label", "onChange", "className", "placeholder", "onClick", "selectedOptionsLabel"]);
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const selectButtonRef = useRef(null);
    const selectItemsRef = useRef(null);
    function handleSelectClick(e) {
        onClick(e);
        if (selectItemsRef.current && !selectItemsRef.current.contains(e.target)) {
            setIsOpen(!isOpen);
        }
    }
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('click', handleWatchClick, { capture: true });
            document.addEventListener('keydown', handleWatchKeyDown);
        }
        else {
            document.removeEventListener('click', handleWatchClick);
            document.removeEventListener('keydown', handleWatchKeyDown);
        }
        return () => {
            document.removeEventListener('click', handleWatchClick);
            document.removeEventListener('keydown', handleWatchKeyDown);
        };
    }, [isOpen]);
    function handleWatchClick(event) {
        const select = selectRef.current;
        if (select && !select.contains(event.target)) {
            setIsOpen(false);
        }
    }
    function handleWatchKeyDown(event) {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                event.stopPropagation();
                event.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                }
                handleChangeFocusOfItems(event.key);
                break;
            case 'Escape':
                setIsOpen(false);
                if (selectButtonRef.current) {
                    selectButtonRef.current.focus();
                }
                break;
            case 'Tab':
                // setting timeout so the dom has time to change activeElement after tab was pressed
                // otherwise activeElement is still same element that triggered the change
                setTimeout(() => {
                    const select = selectRef.current;
                    if (select && !select.contains(document.activeElement)) {
                        setIsOpen(false);
                    }
                }, 1);
                break;
            case 'Home':
            case 'End':
                event.preventDefault();
                handleChangeFocusOfItems(event.key);
        }
    }
    // Manages whether it should open/close when user is focused on select element
    function handleSelectWatchKeyDown(event) {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                if (!isOpen) {
                    event.preventDefault();
                    setIsOpen(true);
                }
                break;
            case 'Enter':
            case ' ':
                setTimeout(() => {
                    const isSelectButton = document.activeElement === selectButtonRef.current;
                    if (isSelectButton) {
                        setIsOpen(!isOpen);
                    }
                }, 1);
                break;
        }
    }
    function handleChangeFocusOfItems(direction) {
        // setting timeout so the dom has time to change activeElement after tab was pressed
        // otherwise activeElement is still same element that triggered the change
        setTimeout(() => {
            const currentEl = document.activeElement;
            const children = (selectButtonRef.current &&
                Array.from(selectButtonRef.current.querySelectorAll('input[data-multiselect-item]'))) ||
                [];
            const getChildAsElement = (index) => children[index];
            if (children.length >= 1 && currentEl) {
                const currentIndex = children.indexOf(currentEl);
                switch (direction) {
                    case 'ArrowDown':
                        if (currentIndex + 1 >= children.length || currentIndex === -1) {
                            getChildAsElement(0).focus();
                        }
                        else {
                            getChildAsElement(currentIndex + 1).focus();
                        }
                        break;
                    case 'ArrowUp':
                        if (currentIndex - 1 < 0) {
                            getChildAsElement(children.length - 1).focus();
                        }
                        else {
                            getChildAsElement(currentIndex - 1).focus();
                        }
                        break;
                    case 'Home':
                        getChildAsElement(0).focus();
                        break;
                    case 'End':
                        getChildAsElement(children.length - 1).focus();
                        break;
                }
            }
        }, 1);
    }
    const selectId = `select-${nanoid()}`;
    const labelId = `label-${nanoid()}`;
    const selectClassNames = classnames({
        [styles.select]: true,
        [className]: className,
        [styles.isOpen]: isOpen,
    });
    const selectMenuClassNames = classnames({
        [styles.multiSelectMenu]: true,
        [styles.isOpen]: isOpen,
    });
    const checkedOptions = options.filter(opt => {
        if (opt.checked === true) {
            return opt;
        }
        return null;
    });
    const activeDescendants = checkedOptions.map(option => option.id).join(' ');
    const activeLabels = selectedOptionsLabel ? selectedOptionsLabel : checkedOptions
        .map((option, index) => {
        const useComma = index + 1 >= checkedOptions.length ? '' : ',';
        return `${option.label}${useComma}`;
    })
        .join(' ');
    const placeholderClassNames = classnames({
        [styles.selectPlaceholderLabel]: true,
        [styles.placeholder]: activeLabels.trim().length === 0,
    });
    return (React.createElement("div", { ref: selectRef, className: styles.multiSelectContainer },
        React.createElement("div", { id: labelId, className: styles.label, "data-testid": "label" }, label),
        React.createElement("div", Object.assign({ ref: selectButtonRef, id: selectId, className: selectClassNames, onClick: handleSelectClick, onKeyDown: handleSelectWatchKeyDown, tabIndex: 0, role: "listbox", "aria-roledescription": "multi select", "aria-expanded": isOpen, "aria-labelledby": labelId, "aria-activedescendant": activeDescendants, "aria-multiselectable": "true" }, rest),
            React.createElement("div", { className: placeholderClassNames, "data-testid": "placeholdOrSelectedOptions" }, activeLabels.trim() || placeholder),
            React.createElement("div", { className: selectMenuClassNames, ref: selectItemsRef }, options.map((option, index) => (React.createElement(MultiSelectItem, Object.assign({}, option, { "aria-posinset": index + 1, "aria-setsize": options.length, key: option.id, onChange: () => onChange(Object.assign(Object.assign({}, option), { checked: !option.checked })), "aria-controls": selectId }))))))));
};
MultiSelect.displayName = 'MultiSelect';

export { MultiSelect, Select };
